import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaCheck, FaSave } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const UserSetupMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const { id } = useParams(); // edit if present
  const isEdit = !!id;

  const [searchParams] = useSearchParams();

  // listing sends: ERP | Payroll | Tally (or upper/lower)
  const moduleFromQueryRaw = searchParams.get("module") || "ERP";
  const moduleFromQuery = String(moduleFromQueryRaw).trim().toUpperCase(); // ERP | PAYROLL | TALLY

  // ADD/EDIT page ASPX ddl_module values:
  // Select, ERP-PAYROLL, Tally
  // In ASPX:
  // - New page defaults Session["Module"]="ERP-PAYROLL" (panel visible)
  // - If opened from listing, Session["Module"] becomes ERP / Payroll / Tally (panel visible ONLY for Payroll)
  const defaultMode = moduleFromQuery === "TALLY" ? "Tally" : "ERP-PAYROLL";
  const [mode, setMode] = useState(defaultMode); // Select | ERP-PAYROLL | Tally

  const moduleForUpdate = useMemo(() => {
    if (moduleFromQuery === "PAYROLL") return "Payroll";
    if (moduleFromQuery === "TALLY") return "Tally";
    return "ERP";
  }, [moduleFromQuery]);

  // dropdown raw
  const [userTypesRaw, setUserTypesRaw] = useState([]);
  const [branchesRaw, setBranchesRaw] = useState([]);
  const [deptsRaw, setDeptsRaw] = useState([]);

  // normalize: IMPORTANT (this was the main reason select was empty)
  const userTypes = useMemo(() => {
    return (userTypesRaw || [])
      .map((u) => {
        const uid =
          u.fld_usertypeid ??
          u.fld_UserTypeId ??
          u.usertypeid ??
          u.usertypeId ??
          u.id;
        const uname =
          u.fld_usertype ??
          u.fld_UserType ??
          u.usertype ??
          u.userType ??
          u.name;
        return { id: String(uid ?? ""), name: String(uname ?? "") };
      })
      .filter((x) => x.id && x.name);
  }, [userTypesRaw]);

  const depts = useMemo(() => {
    return (deptsRaw || [])
      .map((d) => {
        const did =
          d.fld_deptid ??
          d.fld_DeptId ??
          d.deptid ??
          d.deptId ??
          d.id;
        const dname =
          d.fld_departmentname ??
          d.fld_DepartmentName ??
          d.deptname ??
          d.departmentname ??
          d.name;
        return { id: String(did ?? ""), name: String(dname ?? "") };
      })
      .filter((x) => x.id && x.name);
  }, [deptsRaw]);

  const branches = useMemo(() => {
    return (branchesRaw || [])
      .map((b) => {
        const bid =
          b.fld_branchid ??
          b.fld_Branchid ??
          b.fld_branchId ??
          b.branchid ??
          b.branchId ??
          b.id;
        const bname =
          b.fld_branchname ??
          b.fld_BranchName ??
          b.fld_branchName ??
          b.branchname ??
          b.branchName ??
          b.name;
        return { id: String(bid ?? ""), name: String(bname ?? "") };
      })
      .filter((x) => x.id && x.name);
  }, [branchesRaw]);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    userTypeId: "Select",
    deptId: "Select",
    branchId: "Select",
    username: "",
    password: "",
    rePassword: "",
    email: "",
    mobile: "",
    staffName: "",
    hint: "",
    isActive: false,
    isApprove: false,
    // payroll only
    emp: {
      EC: false,
      EM: false,
      FA: false,
      PA: false,
      PS: false,
      PQ: false,
      WS: false,
      EV: false,
      EE: false,
      SS: false,
    },
  });

  // ASPX behavior:
  // - In EDIT: panel shows only if Session["Module"] is Payroll (ERP/Tally hide)
  // - In ADD NEW: panel shows for "ERP-PAYROLL", hides for "Tally"
  const showPayrollPanel = useMemo(() => {
    if (isEdit) return moduleForUpdate === "Payroll";
    return mode !== "Tally"; // ERP-PAYROLL => show, Select => show (ASPX still shows, but validation blocks)
  }, [isEdit, moduleForUpdate, mode]);

  const getAdminId = () => {
    try {
      const a = JSON.parse(localStorage.getItem("admin") || "{}");
      return a?.id ? String(a.id) : "";
    } catch {
      return "";
    }
  };

  const loadDropdowns = async () => {
    try {
      const [ut, br, dp] = await Promise.all([
        axios.get(`${BASE_URL}/api/userSetup/ddl/usertypes`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
        axios.get(`${BASE_URL}/api/userSetup/ddl/branches`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
        axios.get(`${BASE_URL}/api/userSetup/ddl/departments`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
      ]);

      if (ut.data?.success) setUserTypesRaw(ut.data.list || []);
      else setUserTypesRaw([]);

      if (br.data?.success) setBranchesRaw(br.data.list || []);
      else setBranchesRaw([]);

      if (dp.data?.success) setDeptsRaw(dp.data.list || []);
      else setDeptsRaw([]);
    } catch (err) {
      console.error("dropdown load error:", err);
      toast.error("Failed to load dropdowns");
    }
  };

  const fetchById = async (rid) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/userSetupMaster/${rid}`, {
        headers: tenantHeaders(),
        params: tenantParams({ module: moduleForUpdate }), // ERP | Payroll | Tally
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load data");
        return;
      }

      const d = res.data.data || {};

      // emptransferapproval (Payroll only)
      const empMap = {
        EC: false,
        EM: false,
        FA: false,
        PA: false,
        PS: false,
        PQ: false,
        WS: false,
        EV: false,
        EE: false,
        SS: false,
      };

      const empStr = d.fld_emptransferapproval || d.emptransferapproval || "";
      if (empStr) {
        String(empStr)
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
          .forEach((k) => {
            if (Object.prototype.hasOwnProperty.call(empMap, k)) empMap[k] = true;
          });
      }

      const rawMobile = String(d.fld_mobile || d.mobile || "").replace(/\D/g, "").slice(0, 10);

      setForm((prev) => ({
        ...prev,
        userTypeId: d.fld_usertypeid != null ? String(d.fld_usertypeid) : "Select",
        deptId: d.fld_deptid != null ? String(d.fld_deptid) : "Select",
        branchId: d.fld_branchid != null ? String(d.fld_branchid) : "Select",

        username: d.username || d.fld_username || "",
        // NOTE: In ASPX password is encrypted; in your node you said plain for now.
        password: d.password || "",
        rePassword: d.password || "",

        email: d.email || d.fld_email || "",
        mobile: rawMobile,
        staffName: d.fld_staffname || d.staffname || "",
        hint: d.hint || d.fld_hint || "",

        isActive:
          String(d.fld_isActive) === "1" ||
          d.fld_isActive === 1 ||
          d.fld_isActive === true,
        isApprove:
          String(d.fld_isapprove) === "1" ||
          d.fld_isapprove === 1 ||
          d.fld_isapprove === true,

        emp: empMap,
      }));
    } catch (err) {
      console.error("fetchById error:", err);
      toast.error("Failed to load record");
    } finally {
      setLoading(false);
    }
  };

  const checkPossibility = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/userSetupMaster/checkPossibility`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });
      if (res.data?.success && res.data?.canCreate === false && !isEdit) {
        toast.warning(res.data?.message || "You can create only 26 Users", {
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error("checkPossibility error:", err);
    }
  };

  useEffect(() => {
    loadDropdowns();
    checkPossibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit) fetchById(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, moduleForUpdate]);

  // MD rule: if usertype is MD => branch=8 lock branch
  useEffect(() => {
    const selected = userTypes.find((u) => String(u.id) === String(form.userTypeId));
    if (selected?.name === "MD") {
      setForm((p) => ({ ...p, branchId: "8" }));
    }
  }, [form.userTypeId, userTypes]);

  // ASPX: when branch selected it calls CheckPossibility; we do same
  useEffect(() => {
    if (!isEdit && form.branchId && form.branchId !== "Select") {
      checkPossibility();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.branchId]);

  const isMD = useMemo(() => {
    const selected = userTypes.find((u) => String(u.id) === String(form.userTypeId));
    return selected?.name === "MD";
  }, [form.userTypeId, userTypes]);

  const handleMobileChange = (e) => {
    const digitsOnly = String(e.target.value || "").replace(/\D/g, "").slice(0, 10);
    setForm((p) => ({ ...p, mobile: digitsOnly }));
  };

  const validate = () => {
    // NOTE: In ASPX validate() checks ddl_module too.
    if (mode === "Select") return "Pls Select Module Name";
    if (form.userTypeId === "Select") return "Please select UserType";
    if (form.deptId === "Select") return "Please select Department";
    if (form.branchId === "Select") return "Please select branch";
    if (!form.username.trim()) return "Please enter UserName";
    if (!form.password.trim()) return "Please enter Password";
    if (!form.rePassword.trim()) return "Please enter ConfirmPassword";
    if (form.password !== form.rePassword) return "Password does not match";
    if (!form.email.trim()) return "Please enter EmailID";

    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    if (!emailPattern.test(form.email.trim())) return "Please enter valid emailid";

    if (!form.mobile.trim()) return "Please enter Mobile No";
    if (!/^\d{10}$/.test(form.mobile.trim())) return "Please enter a valid 10-digit mobile number";

    return "";
  };

  const empTransferApproval = useMemo(() => {
    const keys = Object.keys(form.emp).filter((k) => form.emp[k]);
    return keys.join(",");
  }, [form.emp]);

  const handleSave = async () => {
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    const adminId = getAdminId();
    if (!adminId) {
      toast.error("Admin not found in localStorage. Please login again.");
      return;
    }

    try {
      setLoading(true);

      if (!isEdit) {
        // INSERT (ASPX: when ddl_module is ERP-PAYROLL it inserts to ERP and Payroll db)
        const payload = {
          mode, // Select | ERP-PAYROLL | Tally
          adminId,
          userTypeId: form.userTypeId,
          deptId: form.deptId,
          branchId: form.branchId,
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim(),
          hint: form.hint || "",
          mobile: form.mobile || "",
          staffName: form.staffName || "",
          isActive: form.isActive,
          isApprove: form.isApprove,
          empTransferApproval: showPayrollPanel ? empTransferApproval : "",
        };

        const res = await axios.post(`${BASE_URL}/api/userSetupMaster/save`, payload, {
          headers: tenantHeaders(),
        });
        if (res.data?.success) {
          toast.success(res.data?.message || "Inserted Successfully");
          navigate("/adminMaster/UserSetupMasterListing");
        } else {
          toast.error(res.data?.message || "Save failed");
        }
      } else {
        // UPDATE (ASPX: updates based on Session["Module"] ERP/Payroll/Tally)
        const payload = {
          module: moduleForUpdate, // ERP | Payroll | Tally
          adminId,
          userTypeId: form.userTypeId,
          deptId: form.deptId,
          branchId: form.branchId,
          username: form.username.trim(),
          password: form.password,
          email: form.email.trim(),
          hint: form.hint || "",
          mobile: form.mobile || "",
          staffName: form.staffName || "",
          isActive: form.isActive,
          isApprove: form.isApprove,
          empTransferApproval: moduleForUpdate === "Payroll" ? empTransferApproval : "",
        };

        const res = await axios.put(`${BASE_URL}/api/userSetupMaster/${id}`, payload, {
          headers: tenantHeaders(),
        });
        if (res.data?.success) {
          toast.success(res.data?.message || "Updated Successfully");
          navigate("/adminMaster/UserSetupMasterListing");
        } else {
          toast.error(res.data?.message || "Update failed");
        }
      }
    } catch (err) {
      console.error("save/update error:", err);
      toast.error(err?.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => navigate("/adminMaster/UserSetupMasterListing");

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">USER SETUP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top actions */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={backToList}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800"
              }`}
            >
              <FaArrowLeft /> Back
            </button>

            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <FaSave />
              {isEdit ? "Update" : "Save"}
            </button>
          </div>

          {/* Header */}
          <div className="mb-6">
            <div className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit ? `Editing ID: ${id}` : "Add New User"}
            </div>
            <div className={`${isDark ? "text-white/60" : "text-gray-500"} text-sm mt-1`}>
              Listing Module: {moduleForUpdate} • Form Mode: {mode}
            </div>
          </div>

          {/* Form */}
          <div className="grid grid-cols-12 gap-5">
            {/* Module dropdown (ASPX add/edit) */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Module Name *</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={isEdit} // ASPX sets module from session in edit; keep consistent
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                } ${isEdit ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <option value="Select">Select</option>
                <option value="ERP-PAYROLL">ERP-PAYROLL</option>
                <option value="Tally">Tally</option>
              </select>
              {isEdit ? (
                <div className={`text-xs mt-1 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                  In edit mode, module comes from listing (ERP / Payroll / Tally).
                </div>
              ) : null}
            </div>

            {/* UserType */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">User Type *</label>
              <select
                value={form.userTypeId}
                onChange={(e) => setForm((p) => ({ ...p, userTypeId: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              >
                <option value="Select">Select</option>
                {userTypes.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Department *</label>
              <select
                value={form.deptId}
                onChange={(e) => setForm((p) => ({ ...p, deptId: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              >
                <option value="Select">Select</option>
                {depts.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Branch */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Branch *</label>
              <select
                value={form.branchId}
                disabled={isMD}
                onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                } ${isMD ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <option value="Select">Select</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>

              {isMD ? (
                <div className={`text-xs mt-1 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                  MD selected → Branch auto set to 8 and locked.
                </div>
              ) : null}
            </div>

            {/* Username */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">User Name *</label>
              <input
                value={form.username}
                disabled={isEdit} // ASPX: username disabled in edit
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                } ${isEdit ? "opacity-70 cursor-not-allowed" : ""}`}
              />
            </div>

            {/* Password */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Password *</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
            </div>

            {/* Confirm Password */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Confirm password *</label>
              <input
                type="password"
                value={form.rePassword}
                onChange={(e) => setForm((p) => ({ ...p, rePassword: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
            </div>

            {/* Email */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Email *</label>
              <input
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
            </div>

            {/* Mobile */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Mobile No *</label>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                pattern="[0-9]{10}"
                placeholder="10-digit mobile number"
                value={form.mobile}
                onChange={handleMobileChange}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData.getData("text") || "")
                    .replace(/\D/g, "")
                    .slice(0, 10);
                  setForm((p) => ({ ...p, mobile: pasted }));
                }}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
              <div className={`text-xs mt-1 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                Only 10 digits allowed{form.mobile ? ` (${form.mobile.length}/10)` : ""}
              </div>
            </div>

            {/* Staff Name */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Staff Name *</label>
              <input
                value={form.staffName}
                onChange={(e) => setForm((p) => ({ ...p, staffName: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
            </div>

            {/* Hint */}
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm font-medium">Hint</label>
              <textarea
                value={form.hint}
                onChange={(e) => setForm((p) => ({ ...p, hint: e.target.value }))}
                className={`mt-1 w-full px-3 py-2 border rounded-lg min-h-[44px] ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                }`}
              />
            </div>

            {/* Active */}
            <div className="col-span-12 md:col-span-3 flex items-center gap-3 mt-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                className="w-4 h-4"
              />
              <div className="text-sm font-medium">Is Active</div>
            </div>

            {/* Approve */}
            <div className="col-span-12 md:col-span-3 flex items-center gap-3 mt-2">
              <input
                type="checkbox"
                checked={form.isApprove}
                onChange={(e) => setForm((p) => ({ ...p, isApprove: e.target.checked }))}
                className="w-4 h-4"
              />
              <div className="text-sm font-medium">Is Approve</div>
            </div>

            {/* Payroll Transfer Approval Panel */}
            {showPayrollPanel ? (
              <div className="col-span-12 mt-4">
                <div
                  className={`p-4 rounded-xl border ${
                    isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div className="font-semibold mb-3">Employee Transfer Approval</div>
                  <div className="flex flex-wrap gap-6">
                    {Object.keys(form.emp).map((k) => (
                      <label key={k} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.emp[k]}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              emp: { ...p.emp, [k]: e.target.checked },
                            }))
                          }
                          className="w-4 h-4"
                        />
                        {k}
                      </label>
                    ))}
                  </div>
                  {isEdit && moduleForUpdate !== "Payroll" ? (
                    <div className={`text-xs mt-2 ${isDark ? "text-white/60" : "text-gray-500"}`}>
                      (This panel is only for Payroll users in ASPX.)
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <FaCheck />
              {isEdit ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default UserSetupMaster;